/**
 * <exit-intent-survey>
 *
 * Extends the theme's shared `ModalDialog` (global.js) purely for its
 * show()/hide() plumbing: body scroll lock, focus trap, Escape-to-close and
 * click-outside-to-close all come from the base class for free. Everything
 * below decides WHETHER and WHEN to call show().
 *
 * The answer is tracked as a Klaviyo custom event, the same way
 * snippets/klaviyo-tracking.liquid already tracks "Added to Cart" etc: push
 * into the window.klaviyo queue the onsite SDK drains once it loads. There
 * is no other backend and nothing here ever calls fetch() against a
 * third-party endpoint.
 *
 * PDPs are excluded from normal display. With experiment_mode enabled, Liquid
 * permits an explicit activation only on Italian PDPs. Non-PDP pages still
 * follow the normal theme setting. There is no dependency on Intelligems:
 * the external caller starts the same standalone triggers and submission logic.
 * See docs/exit-intent-survey.md for the Intelligems snippet.
 *
 * Gating, in order:
 *   0. PDP exclusion -> only the configured Italian experiment can override it.
 *   1. isSuppressedTemplate (cart page) -> never even arm a listener.
 *   2. requireCartOrPdp -> only arm once the shopper has a non-empty cart OR
 *      has viewed at least one PDP this browser (localStorage, not
 *      session-scoped, so it survives the shopper leaving and coming back).
 *   3. At the moment a trigger actually fires, re-check that no other
 *      known popup already ran this session (best-effort: the only other
 *      exit-style popup in this theme is <market-suggestion>, which records
 *      its own dismissal under MARKET_SUGGESTION_KEY below).
 *
 * There is NO frequency cap across page loads right now -- it can fire once
 * on each eligible page load. A 30-day "don't show again"
 * localStorage flag was here and got pulled while the popup itself was
 * still being tuned, so testing wasn't fighting a cooldown on top of
 * everything else. Re-add it (a #isDismissed()/#dismissForDays() pair,
 * gated the same way requireCartOrPdp is) before this goes live for real.
 *
 * Triggers:
 *   Desktop  -- document `mouseleave` with clientY near the top of the
 *               viewport, armed only after a short delay so a stray cursor
 *               exit while the page is still loading doesn't fire it.
 *   Touch    -- no mouseleave exists, so two substitutes: a fast upward
 *               scroll while still near the top of the page (heading for the
 *               address bar / another tab), and plain inactivity after
 *               `mobileIdleSeconds` with no touch/scroll/click/key input.
 *
 * The question and its answer options are server-rendered directly in
 * sections/exit-intent-survey.liquid -- this file never builds that markup,
 * it only reads `data-reason-key` off each radio (the stable, handle-ized
 * value sent to Klaviyo instead of the editable label). The free-text field
 * is always visible and always optional, regardless of which option (if
 * any) is selected.
 */
(function () {
  if (typeof ModalDialog === 'undefined') return;

  class ExitIntentSurvey extends ModalDialog {
    static METRIC_NAME = 'Exit Survey Response';
    static PDP_VIEWED_KEY = 'sheko:pdp-viewed';
    static MARKET_SUGGESTION_KEY = 'shop-redirect-popup-closed';
    static ARM_DELAY_MS = 15000;
    static EXIT_ZONE_PX = 100;
    static FAST_SCROLL_UP_PX = 60;
    static FAST_SCROLL_TOP_PX = 400;

    #settings = null;
    #context = null;
    #started = false;
    #debug = false;
    #armed = false;
    #triggered = false;
    #idleTimer = null;
    #lastScrollY = 0;
    #onMouseLeaveBound = null;
    #onScrollBound = null;
    #onActivityBound = null;

    // NOT connectedCallback: ModalDialog's own connectedCallback moves this
    // element to document.body on first connect, which reparents it --
    // reparenting fires disconnectedCallback then connectedCallback again.
    // Starting from the constructor (guaranteed to run exactly once) avoids
    // that entirely instead of guarding against a double-start.
    constructor() {
      super();
      this.#settings = this.#readSettings();

      // Preserve normal eligibility on later pages, including when this PDP
      // belongs to the experiment's control group. This records a page visit,
      // not survey activation; triggers and form handling remain inactive.
      if (this.#settings?.isPdpTemplate) this.#markPdpViewed();
      if (this.#settings?.autoStart !== false) this.activate();
    }

    // Starts the normal trigger sequence; never opens the dialog immediately.
    // Scope checks also apply in debug mode. Repeated calls cannot bind another
    // form handler or start another timer, even while eligibility is awaiting a cart.
    async activate({ pdpOnly = false, country, language } = {}) {
      if (!this.#settings) {
        this.#log('no settings found — the section itself did not render (disabled, or no answer options configured). Nothing below can run.');
        return false;
      }
      if (pdpOnly && !this.#settings.isPdpTemplate) return false;
      if (country && country !== this.#settings.country) return false;
      if (language && language !== this.#settings.language) return false;
      if (this.#settings.isPdpTemplate && !this.#settings.pdpExperiment) return false;
      if (this.#started) return false;
      this.#started = true;

      // ?exit_intent_debug in the URL skips every gate below (suppression,
      // cart/PDP targeting, the popup-already-shown check) and shrinks the
      // arm delay to 500ms, so a merchant testing the popup
      // only has to get the mouse movement right, not also satisfy every
      // targeting condition. It never skips the movement itself -- see the
      // #onMouseLeave/#onScroll doc comments for what still has to happen.
      this.#debug = this.#isDebugMode();
      if (this.#debug) this.#log('debug mode ON (?exit_intent_debug) — gates bypassed, arm delay is 500ms.');

      if (!this.#debug) {
        if (this.#settings.isSuppressedTemplate) {
          this.#log('suppressed: current template is "cart". Test on a different page, or use ?exit_intent_debug=1.');
          return false;
        }

        if (this.#settings.requireCartOrPdp) {
          const eligible = this.#hasViewedPdp() || (await this.#hasCartItems());
          if (!eligible) {
            this.#log(
              'not eligible: no product page viewed yet in this browser AND the cart is empty. ' +
                'Visit a product page or add something to the cart first, turn off "Only show to ' +
                'shoppers with a cart or a viewed product" in the section settings, or test with ' +
                '?exit_intent_debug=1 to skip this check entirely.'
            );
            return false;
          }
        }
      }

      this.#log(`eligible — arming triggers in ${this.#debug ? 500 : ExitIntentSurvey.ARM_DELAY_MS}ms.`);
      this.#bindForm();
      this.#bindDiscount();
      this.#armTriggers();
      return true;
    }

    #isDebugMode() {
      try {
        return new URLSearchParams(window.location.search).has('exit_intent_debug');
      } catch (error) {
        return false;
      }
    }

    #log(message) {
      console.info(`[exit-intent-survey] ${message}`);
    }

    // -- eligibility ----------------------------------------------------------

    #markPdpViewed() {
      try {
        localStorage.setItem(ExitIntentSurvey.PDP_VIEWED_KEY, String(Date.now()));
      } catch (error) {
        // Storage unavailable (private mode, blocked cookies): eligibility
        // falls back to the cart check only.
      }
    }

    #hasViewedPdp() {
      try {
        return Boolean(localStorage.getItem(ExitIntentSurvey.PDP_VIEWED_KEY));
      } catch (error) {
        return false;
      }
    }

    async #hasCartItems() {
      try {
        const response = await fetch(`${window.routes?.cart_url ?? '/cart'}.js`);
        const cart = await response.json();
        return (cart?.item_count ?? 0) > 0;
      } catch (error) {
        return false;
      }
    }

    // -- cross-popup suppression --------------------------

    #anotherPopupAlreadyShown() {
      try {
        return Boolean(sessionStorage.getItem(ExitIntentSurvey.MARKET_SUGGESTION_KEY));
      } catch (error) {
        return false;
      }
    }

    // -- triggers ---------------------------------------------------------------

    #armTriggers() {
      // Delayed as a whole, not just the desktop branch: a page-load scroll
      // restore or an anchor jump can look exactly like the mobile "fast
      // scroll up" signal in the first instant, so that listener needs the
      // same dwell time as the desktop one before it's trusted.
      window.setTimeout(() => {
        if (this.#armed || this.#triggered) return;
        this.#armed = true;

        const isTouch = matchMedia('(pointer: coarse)').matches;

        if (isTouch) {
          const seconds = this.#settings.mobileIdleSeconds || 45;
          this.#log(
            `armed (touch device): move the mouse — er, finger — fast upward while still ` +
              `near the top of the page, or just stop touching the screen for ${seconds}s.`
          );
          this.#lastScrollY = window.scrollY;
          this.#onScrollBound = this.#onScroll.bind(this);
          window.addEventListener('scroll', this.#onScrollBound, { passive: true });

          this.#onActivityBound = this.#resetIdleTimer.bind(this);
          ['touchstart', 'scroll', 'click', 'keydown'].forEach((type) =>
            window.addEventListener(type, this.#onActivityBound, { passive: true })
          );
          this.#resetIdleTimer();
        } else {
          this.#log(
            `armed (desktop): move the mouse up and OUT of the browser window through the ` +
              `top edge (within ${ExitIntentSurvey.EXIT_ZONE_PX}px of the top) — like reaching ` +
              `for the tab bar. Hovering near the top without leaving the window won't fire it.`
          );
          this.#onMouseLeaveBound = this.#onMouseLeave.bind(this);
          document.addEventListener('mouseleave', this.#onMouseLeaveBound);
        }
      }, this.#debug ? 500 : ExitIntentSurvey.ARM_DELAY_MS);
    }

    #disarmTriggers() {
      if (this.#onMouseLeaveBound) document.removeEventListener('mouseleave', this.#onMouseLeaveBound);
      if (this.#onScrollBound) window.removeEventListener('scroll', this.#onScrollBound);
      if (this.#onActivityBound) {
        ['touchstart', 'scroll', 'click', 'keydown'].forEach((type) =>
          window.removeEventListener(type, this.#onActivityBound)
        );
      }
      window.clearTimeout(this.#idleTimer);
    }

    #onMouseLeave(event) {
      // relatedTarget is null specifically when the pointer leaves the
      // window itself (toward browser chrome/another app) rather than onto
      // some other in-page element (an iframe, a native <select> — anything
      // with its own hit box) that also emits mouseleave on the document.
      if (event.relatedTarget) {
        this.#log('mouseleave ignored: it left onto another in-page element, not out of the window.');
        return;
      }
      if (event.clientY > ExitIntentSurvey.EXIT_ZONE_PX) {
        this.#log(
          `mouseleave ignored: left at clientY=${event.clientY}, needs to be <= ${ExitIntentSurvey.EXIT_ZONE_PX}. Go further up before leaving the window.`
        );
        return;
      }
      this.#log('mouseleave qualified — triggering.');
      this.#trigger();
    }

    #onScroll() {
      const y = window.scrollY;
      const delta = this.#lastScrollY - y; // positive: scrolled up
      this.#lastScrollY = y;
      if (y < ExitIntentSurvey.FAST_SCROLL_TOP_PX && delta > ExitIntentSurvey.FAST_SCROLL_UP_PX) {
        this.#log('fast upward scroll near the top qualified — triggering.');
        this.#trigger();
      }
    }

    #resetIdleTimer() {
      window.clearTimeout(this.#idleTimer);
      const seconds = this.#settings.mobileIdleSeconds || 45;
      this.#idleTimer = window.setTimeout(() => this.#trigger(), seconds * 1000);
    }

    #trigger() {
      if (this.#triggered) return;
      if (!this.#debug && this.#anotherPopupAlreadyShown()) {
        this.#log('suppressed: the market-suggestion popup already ran this session.');
        return;
      }

      this.#log('opening now.');
      this.#triggered = true;
      this.#disarmTriggers();

      // Fire-and-forget: submission doesn't need to wait on this, and the
      // dialog should never sit there un-opened because a fetch is slow.
      this.#populateContext();

      this.show(document.activeElement instanceof HTMLElement ? document.activeElement : document.body);
    }

    // -- context (sent along with the answer) ----------------------------------

    async #populateContext() {
      const cart = await this.#cart();
      this.#context = {
        cartValue: cart ? cart.total_price / 100 : null,
        cartCurrency: cart?.currency ?? null,
        device: matchMedia('(pointer: coarse)').matches ? 'mobile' : 'desktop',
        source: this.#trafficSource(),
      };
    }

    async #cart() {
      try {
        const response = await fetch(`${window.routes?.cart_url ?? '/cart'}.js`);
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    #trafficSource() {
      try {
        const utmSource = new URLSearchParams(window.location.search).get('utm_source');
        if (utmSource) return utmSource;
        if (document.referrer) return new URL(document.referrer).hostname;
      } catch (error) {
        // fall through to 'direct'
      }
      return 'direct';
    }

    // -- form / submission --------------------------------------------------

    #bindDiscount() {
      const code = this.querySelector('[data-exit-intent-survey-discount-code]');
      const button = this.querySelector('[data-exit-intent-survey-copy]');
      const status = this.querySelector('[data-exit-intent-survey-copy-status]');
      if (!code || !button || !status) return;

      button.addEventListener('click', async () => {
        if (button.disabled) return;
        button.disabled = true;
        button.removeAttribute('data-copied');
        status.textContent = '';
        try {
          await navigator.clipboard.writeText(code.value);
          button.setAttribute('data-copied', 'true');
          status.textContent = button.dataset.copiedLabel;
        } catch (error) {
          code.focus();
          code.select();
          status.textContent = button.dataset.failedLabel;
        } finally {
          button.disabled = false;
        }
      });
    }

    #bindForm() {
      const form = this.querySelector('[data-exit-intent-survey-form]');
      const freetext = this.querySelector('[data-exit-intent-survey-freetext]');
      if (!form) return;

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.#submit(form, freetext);
      });
    }

    #submit(form, freetext) {
      const selected = form.querySelector('input[name="exit-intent-survey-option"]:checked');
      if (!selected) return;

      const submitButton = form.querySelector('.exit-intent-survey__submit');
      if (submitButton) submitButton.disabled = true;

      this.#track({
        reason: selected.dataset.reasonKey || selected.value,
        reason_label: selected.value,
        survey_profile: this.#settings.surveyProfile ?? 'configured',
        product_id: this.#settings.productId ?? null,
        reason_text: freetext ? freetext.value.trim() : '',
        page_url: window.location.href,
        page_template: this.#settings.template ?? null,
        cart_value: this.#context?.cartValue ?? null,
        cart_currency: this.#context?.cartCurrency ?? null,
        device: this.#context?.device ?? null,
        traffic_source: this.#context?.source ?? null,
      });

      const thanks = this.querySelector('[data-exit-intent-survey-thanks]');
      if (thanks) thanks.hidden = false;
      form.hidden = true;

      if (this.querySelector('[data-exit-intent-survey-offer]')) {
        thanks?.focus();
      } else {
        window.setTimeout(() => this.hide(), 1600);
      }
    }

    // klaviyo-tracking.liquid's own sendTrack(), reproduced here rather than
    // shared: that helper lives inside an IIFE in an inline <script> and
    // isn't exposed on window, so there is nothing to import from this
    // external file. Same queue, same fallback order.
    #track(properties) {
      try {
        if (window.klaviyo && typeof window.klaviyo.track === 'function') {
          window.klaviyo.track(ExitIntentSurvey.METRIC_NAME, properties);
          return;
        }
        window.klaviyo = window.klaviyo || [];
        window.klaviyo.push(['track', ExitIntentSurvey.METRIC_NAME, properties]);
      } catch (error) {
        // Klaviyo blocked or unavailable (consent, ad-blocker): the shopper
        // still gets the thank-you and the popup still closes either way.
      }
    }

    // -- settings ---------------------------------------------------------------

    #readSettings() {
      const node = document.querySelector('[data-exit-intent-survey-settings]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        return null;
      }
    }
  }

  if (!customElements.get('exit-intent-survey')) {
    customElements.define('exit-intent-survey', ExitIntentSurvey);
  }
})();
